import { coreAPI } from './client';
import type {
  ConversationListItem,
  ConversationDetail,
  Message,
  SendMessageDto,
  CreateConversationDto,
  CreateConversationResponse,
  UpdateConversationDto,
  MarkAsReadResponse,
  CloseConversationResponse,
  TypingResponse,
  ConversationsQueryParams,
  MessagesQueryParams,
  UploadAttachmentResponse,
  UnreadCounts,
  ViewerContext,
} from '@/types/messenger';

function buildConversationsQuery(params?: ConversationsQueryParams): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  if (params.context) sp.set('context', params.context);
  if (params.type) sp.set('type', params.type);
  if (params.status) sp.set('status', params.status);
  if (params.merchantGuid) sp.set('merchantGuid', params.merchantGuid);
  if (params.orderGuid) sp.set('orderGuid', params.orderGuid);
  if (params.page) sp.set('page', String(params.page));
  if (params.limit) sp.set('limit', String(params.limit));
  const query = sp.toString();
  return query ? `?${query}` : '';
}

/**
 * `?context=merchant:<g>` for an action on one thread — act as that shop. `personal` adds
 * nothing: without a named shop the backend acts as the caller's own buyer row first (ADR-002 D5).
 */
function actingQuery(context?: ViewerContext): string {
  return context && context !== 'personal' ? `?context=${encodeURIComponent(context)}` : '';
}

function buildMessagesQuery(params?: MessagesQueryParams): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  if (params.limit) sp.set('limit', String(params.limit));
  if (params.before) sp.set('before', params.before);
  const query = sp.toString();
  return query ? `?${query}` : '';
}

export const conversationsApi = {
  // --- Conversations ---

  list: (params?: ConversationsQueryParams) =>
    coreAPI.get<ConversationListItem[]>(`/conversations${buildConversationsQuery(params)}`),

  get: (conversationGuid: string, context?: ViewerContext) =>
    coreAPI.get<ConversationDetail>(`/conversations/${conversationGuid}${actingQuery(context)}`),

  /** Every badge of the caller, per context and type (MSG-INBOX-TABS.5 AC-2) */
  unreadCounts: () => coreAPI.get<UnreadCounts>('/conversations/unread-counts'),

  create: (data: CreateConversationDto) =>
    coreAPI.post<CreateConversationResponse>('/conversations', data),

  update: (conversationGuid: string, data: Partial<UpdateConversationDto>) =>
    coreAPI.patch<ConversationDetail>(`/conversations/${conversationGuid}`, data),

  markAsRead: (conversationGuid: string, context?: ViewerContext) =>
    coreAPI.post<MarkAsReadResponse>(
      `/conversations/${conversationGuid}/read${actingQuery(context)}`
    ),

  close: (conversationGuid: string, reason?: string, context?: ViewerContext) =>
    coreAPI.post<CloseConversationResponse>(
      `/conversations/${conversationGuid}/close${actingQuery(context)}`,
      reason ? { reason } : undefined
    ),

  sendTyping: (conversationGuid: string, context?: ViewerContext) =>
    coreAPI.post<TypingResponse>(
      `/conversations/${conversationGuid}/typing${actingQuery(context)}`
    ),

  // --- Messages ---

  getMessages: (conversationGuid: string, params?: MessagesQueryParams) =>
    coreAPI.get<Message[]>(
      `/conversations/${conversationGuid}/messages${buildMessagesQuery(params)}`
    ),

  sendMessage: (conversationGuid: string, data: SendMessageDto) =>
    coreAPI.post<Message>(`/conversations/${conversationGuid}/messages`, data),

  markMessageRead: (messageGuid: string) =>
    coreAPI.post<Message>(`/messages/${messageGuid}/read`),

  deleteMessage: (messageGuid: string) =>
    coreAPI.delete<{ deleted: boolean; messageGuid: string }>(`/messages/${messageGuid}`),

  // --- Attachments ---

  uploadAttachment: (conversationGuid: string, formData: FormData) =>
    coreAPI.postFormData<UploadAttachmentResponse>(
      `/conversations/${conversationGuid}/attachments`,
      formData
    ),
};
